import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { tasksApi, leadsApi } from '../api/endpoints';
import { useAuth } from '../context/AuthContext';
import { DeleteButton } from '../components/DeleteButton';
import { toast } from 'sonner';
import { useTeamOptions } from '../hooks/useTeamOptions';

const priorityColor = {
  High: 'bg-rose-50 text-rose-700 border-rose-200',
  Medium: 'bg-amber-50 text-amber-700 border-amber-200',
  Low: 'bg-sky-50 text-sky-700 border-sky-200',
};

/**
 * FUZZY DATE MATCHER
 * Handles date formats with or without times (e.g., "YYYY-MM-DD" or "YYYY-MM-DD 10:00")
 */
const isDateToday = (apiDateString) => {
  if (!apiDateString) return false;
  const str = String(apiDateString).trim().toLowerCase();
  
  if (str.includes('today')) return true;

  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const html5Date = `${yyyy}-${mm}-${dd}`;
  
  if (str.includes(html5Date)) return true;

  try {
    // Isolate the date part if a time is included (e.g., "2026-05-27 10:00")
    const datePart = str.split('T')[0].split(' ')[0];
    const parsed = new Date(datePart);
    if (!isNaN(parsed.getTime())) {
      return parsed.toDateString() === today.toDateString();
    }
  } catch (e) {
    // Ignore invalid formats
  }
  return false;
};

const TaskFormDialog = ({ leads, onSaved, trigger, initial }) => {
  const isEdit = !!initial;
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', due: '', priority: 'Medium', status: 'Pending',
    owner: '', related: '',
  });
  const team = useTeamOptions();

  useEffect(() => {
    if (open) {
      setForm({
        title: initial?.title || '',
        due: initial?.due || '',
        priority: initial?.priority || 'Medium',
        status: initial?.status || 'Pending',
        owner: initial?.owner || '',
        related: initial?.related || '',
      });
    }
    // eslint-disable-next-line
  }, [open, initial]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title || !form.due) { toast.error('Title and due date are required'); return; }
    setSaving(true);
    try {
      const saved = isEdit
        ? await tasksApi.update(initial.id, form)
        : await tasksApi.create(form);
      onSaved(saved, isEdit);
      toast.success(isEdit ? 'Task updated' : 'Task created');
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save task');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{isEdit ? 'Edit Task' : 'Create New Task'}</DialogTitle>
          <DialogDescription>{isEdit ? 'Update task details.' : 'Add a follow-up or to-do for your team.'}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Label>Task Title *</Label><Input value={form.title} onChange={e => update('title', e.target.value)} placeholder="Follow up with..." /></div>
          <div><Label>Due Date & Time *</Label><Input type="datetime-local" value={form.due.replace(' ', 'T')} onChange={e => update('due', e.target.value.replace('T', ' '))} /></div>
          <div>
            <Label>Priority</Label>
            <Select value={form.priority} onValueChange={v => update('priority', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{['High','Medium','Low'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={v => update('status', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{['Pending','In Progress','Done'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Owner</Label>
            <Select value={form.owner} onValueChange={v => update('owner', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{team.map(u => <SelectItem key={u.id} value={u.name}>{u.name} — {u.role}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label>Related Lead (optional)</Label>
            <Input value={form.related} onChange={e => update('related', e.target.value)} placeholder="e.g. L1003" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="bg-brand-navy hover:bg-[#13294A] text-white" onClick={submit} disabled={saving}>{saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Task')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Tasks = () => {
  const { hasPermission, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const canCreate = hasPermission('tasks', 'create');
  const canEdit = hasPermission('tasks', 'edit');
  const canDelete = hasPermission('tasks', 'delete');

  const [data, setData] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  // Router Driven Filter States
  const [applyTodayFilter, setApplyTodayFilter] = useState(false);
  const [usedFallback, setUsedFallback] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [t, l] = await Promise.all([tasksApi.list(), leadsApi.list().catch(() => [])]);
        setData(t); setLeads(l);
      } catch (e) {
        console.error('Failed to load tasks', e);
        toast.error('Failed to load tasks');
      } finally { setLoading(false); }
    })();
  }, []);

  // Intercept routing payload from Dashboard
  useEffect(() => {
    setApplyTodayFilter(location.state?.filterDate === 'today');
    setUsedFallback(false);
  }, [location.state]);

  const toggle = async (t) => {
    if (!canEdit) return;
    const newStatus = t.status === 'Done' ? 'Pending' : 'Done';
    setData(d => d.map(x => x.id === t.id ? { ...x, status: newStatus } : x));
    try { await tasksApi.update(t.id, { status: newStatus }); }
    catch (e) { toast.error('Failed to update task'); setData(d => d.map(x => x.id === t.id ? { ...x, status: t.status } : x)); }
  };

  const onSaved = (saved, isEdit) => {
    if (isEdit) setData(d => d.map(x => x.id === saved.id ? saved : x));
    else setData(d => [saved, ...d]);
  };

  const onDelete = async (t) => {
    await tasksApi.remove(t.id);
    setData(d => d.filter(x => x.id !== t.id));
    toast.success('Task removed');
  };

  const clearFilters = () => {
    setApplyTodayFilter(false);
    setUsedFallback(false);
    navigate('.', { replace: true, state: null });
  };

  // Check if current logged-in user is an Administrator or Team Co-ordinator
  const isAdmin = user?.role === 'Admin' || user?.role === 'Team Co-ordinator';

  // Filters visibility: Admin roles see all tasks, standard users see only their assignments
  const visibleTasks = useMemo(() => {
    return data.filter(t => {
      if (isAdmin) return true;
      return t.owner === user?.name;
    });
  }, [data, isAdmin, user]);

  // Execute Dynamic Route Filtering
  const filteredTasks = useMemo(() => {
    let result = visibleTasks;

    if (applyTodayFilter) {
      const exactMatches = result.filter(t => isDateToday(t.due));
      
      if (exactMatches.length === 0 && result.length > 0) {
        setUsedFallback(true);
        // Fallback: Show all open tasks if dummy data has no specific tasks for today
        result = result.filter(t => t.status !== 'Done');
      } else {
        setUsedFallback(false);
        result = exactMatches;
      }
    }

    return result;
  }, [visibleTasks, applyTodayFilter]);

  const groups = {
    Pending: filteredTasks.filter(t => t.status === 'Pending'),
    'In Progress': filteredTasks.filter(t => t.status === 'In Progress'),
    Done: filteredTasks.filter(t => t.status === 'Done'),
  };

  const todayUIString = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <div className="space-y-5">
      
      {/* Dynamic Filter Banner */}
      {applyTodayFilter && (
        <div className={`p-3 border rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs font-medium animate-in fade-in zoom-in duration-300 ${usedFallback ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
          <div className="flex items-center gap-2">
            <Icons.Filter className={`w-4 h-4 shrink-0 ${usedFallback ? 'text-amber-600' : 'text-blue-600'}`} />
            <span>
              {usedFallback ? (
                <>No specific tasks matched today's date in dummy data. Falling back to show <strong>All Open Tasks</strong>.</>
              ) : (
                <>Showing tasks strictly due <strong>today ({todayUIString})</strong>.</>
              )}
            </span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={clearFilters} 
            className={`h-7 font-bold uppercase tracking-wider text-[10px] shrink-0 bg-white shadow-sm ${usedFallback ? 'text-amber-700 hover:bg-amber-100 hover:text-amber-900' : 'text-blue-700 hover:bg-blue-100 hover:text-blue-900'}`}
          >
            Clear Filter
          </Button>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        {[
          { label: 'Pending', count: groups.Pending.length, color: 'text-amber-600', icon: 'Clock' },
          { label: 'In Progress', count: groups['In Progress'].length, color: 'text-blue-600', icon: 'Loader' },
          { label: 'Completed', count: groups.Done.length, color: 'text-emerald-600', icon: 'CheckCircle2' },
        ].map(s => {
          const Icon = Icons[s.icon];
          return (
            <Card key={s.label} className="border-slate-200 card-lift">
              <CardContent className="p-3 sm:p-4 flex items-center justify-between">
                <div>
                  <div className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wide">{s.label}</div>
                  <div className="font-display text-xl sm:text-2xl font-bold mt-1">{s.count}</div>
                </div>
                <Icon className={`w-6 h-6 sm:w-7 sm:h-7 ${s.color}`} />
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-3 sm:p-4 flex items-center justify-between gap-2">
          <div className="font-display text-base sm:text-lg font-bold text-slate-900">
            {applyTodayFilter ? "Today's Tasks" : "My Tasks"}
          </div>
          {canCreate && (
            <TaskFormDialog
              leads={leads}
              onSaved={onSaved}
              trigger={
                <Button className="bg-brand-navy hover:bg-[#13294A] text-white" size="sm">
                  <Icons.Plus className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">New Task</span>
                </Button>
              }
            />
          )}
        </CardContent>
      </Card>

      {loading && <Skeleton className="h-64" />}
      
      {!loading && filteredTasks.length === 0 && (
        <Card className="border-slate-200 border-dashed bg-slate-50/50">
          <CardContent className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Icons.CheckSquare className="w-12 h-12 mb-3 text-slate-300" />
            <h3 className="text-slate-800 font-medium text-lg">All caught up!</h3>
            <p className="text-sm mt-1 text-center max-w-sm">
              You don't have any tasks matching these criteria. Time to take a breather or create a new task.
            </p>
            {applyTodayFilter && (
              <Button variant="link" onClick={clearFilters} className="mt-4 text-brand-navy">
                View all upcoming tasks
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {!loading && Object.entries(groups).map(([k, list]) => list.length > 0 && (
        <div key={k}>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 px-1">{k} • {list.length}</div>
          <Card className="border-slate-200">
            <CardContent className="p-0 divide-y divide-slate-100">
              {list.map(t => (
                <div key={t.id} className="flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 hover:bg-slate-50 transition-colors">
                  <Checkbox checked={t.status === 'Done'} onCheckedChange={() => toggle(t)} disabled={!canEdit} />
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${t.status === 'Done' ? 'line-through text-slate-400' : 'text-slate-800'} truncate`}>
                      {t.title}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
                      <span className="flex items-center gap-1"><Icons.Calendar className="w-3 h-3" /> {t.due}</span>
                      {t.related && <span className="flex items-center gap-1 hidden sm:flex"><Icons.Link2 className="w-3 h-3" /> {t.related}</span>}
                    </div>
                  </div>
                  <Badge variant="outline" className={`${priorityColor[t.priority]} hidden sm:inline-flex`}>{t.priority}</Badge>
                  <Avatar className="w-7 h-7 hidden sm:flex">
                    <AvatarFallback className="text-[10px] bg-brand-navy text-white">
                      {(t.owner || '').split(' ').map(n => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex items-center gap-0.5">
                    {canEdit && (
                      <TaskFormDialog
                        leads={leads}
                        initial={t}
                        onSaved={onSaved}
                        trigger={<Button size="icon" variant="ghost" title="Edit"><Icons.Pencil className="w-4 h-4" /></Button>}
                      />
                    )}
                    {canDelete && (
                      <DeleteButton title="Delete task?" description="This will permanently remove this task." onConfirm={() => onDelete(t)} />
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      ))}
    </div>
  );
};

export default Tasks;